import {
  ApolloClient,
  from as apolloFrom,
  HttpLink,
  InMemoryCache,
  NormalizedCacheObject,
} from '@apollo/client';
import { CombinedGraphQLErrors, ServerError } from '@apollo/client/errors';
import { ApolloLink } from '@apollo/client/link';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { persistCache } from 'apollo-cache-persist';
import { PersistedData, PersistentStorage } from 'apollo-cache-persist/types';
import fetch from 'cross-fetch';
import { from as rxFrom } from 'rxjs';
import { filter, mergeMap } from 'rxjs/operators';
import { onLogout } from 'service/gtm';
import { getItem, removeItem, setItem } from 'utils/local-storage-helpers';
import { getToken } from 'utils/token';
import { REFRESH_TOKEN } from './mutations/user-mutations';

export const cache = new InMemoryCache();

type RefreshTokenPayload = {
  refreshToken: {
    token: string;
    refreshToken: string;
  };
};

const getNewToken = async (): Promise<{ token?: string; refreshToken?: string }> => {
  const { token } = getToken();
  const refreshToken = getItem('refreshToken');

  try {
    const response = await ectorV4ClientWithoutAuth.mutate<RefreshTokenPayload>({
      mutation: REFRESH_TOKEN,
      variables: {
        token,
        refreshToken,
      },
    });

    if (!response?.data?.refreshToken) {
      return {};
    }

    return response.data.refreshToken;
  } catch (error) {
    removeItem('token');
    removeItem('refreshToken');
    onLogout();
    window.location.reload();

    return {};
  }
};

const rerunOperationAfterRefreshToken = (
  operation: ApolloLink.Operation,
  forward: ApolloLink.ForwardFunction,
): ReturnType<ApolloLink.ForwardFunction> | void => {
  const { token } = getToken();
  const refreshToken = getItem('refreshToken');

  if (!token || !refreshToken) {
    removeItem('token');
    removeItem('refreshToken');
    window.location.reload();

    return;
  }

  return rxFrom(getNewToken()).pipe(
    filter((value): value is { token: string; refreshToken: string } =>
      Boolean(value?.token && value?.refreshToken),
    ),
    mergeMap(({ token: nextToken, refreshToken: nextRefreshToken }) => {
      if (!nextToken || !nextRefreshToken) {
        removeItem('token');
        removeItem('refreshToken');
        window.location.reload();
      }

      setItem('token', nextToken);
      setItem('refreshToken', nextRefreshToken);

      const oldHeaders = operation.getContext().headers;
      // modify the operation context with a new token
      operation.setContext({
        headers: {
          ...oldHeaders,
          authorization: `Bearer ${nextToken}`,
        },
      });

      // retry the request, returning the new observable
      return forward(operation);
    }),
  ) as ReturnType<ApolloLink.ForwardFunction>;
};

if (typeof window !== `undefined`) {
  persistCache({
    cache,
    storage: window.localStorage as PersistentStorage<PersistedData<NormalizedCacheObject>>,
  });
}

const errorLink = onError(({ error, operation, forward }) => {
  if (CombinedGraphQLErrors.is(error)) {
    error.errors.forEach(({ extensions }) => {
      if (extensions?.code === 'UNAUTHENTICATED') {
        return rerunOperationAfterRefreshToken(operation, forward);
      }
    });
  }

  if (ServerError.is(error) && error.statusCode) {
    if (error.statusCode === 401) {
      return rerunOperationAfterRefreshToken(operation, forward);
    }
  }
});

const authLink = setContext(() => {
  const { token } = getToken();

  return {
    headers: {
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      'X-ECTOR-ORIGIN': 'front',
    },
  };
});

const link = apolloFrom([
  errorLink,
  authLink,
  new HttpLink({
    uri: `${process.env.GATSBY_V3_API_URL}/graphql`,
    fetch,
  }),
]);

const ectorV4Link = apolloFrom([
  errorLink,
  authLink,
  new HttpLink({
    uri: `${process.env.GATSBY_V4_API_URL}/graphql`,
    fetch,
  }),
]);

export const ectorV4Client = new ApolloClient({
  link: ectorV4Link,
  ssrMode: typeof window === `undefined`,
  cache,
});

export const ectorV4ClientWithoutAuth = new ApolloClient({
  link: apolloFrom([
    new HttpLink({
      uri: `${process.env.GATSBY_V4_API_URL}/graphql`,
      fetch,
    }),
  ]),
  ssrMode: typeof window === `undefined`,
  cache,
});

export default new ApolloClient({
  link,
  ssrMode: typeof window === `undefined`,
  cache,
});
